/**
 * Store configuration composable
 * Determines which store context the app is running in and provides store-specific config.
 *
 * Store ID can be set via:
 * 1. Hardcoded DEV_STORE_ID constant (for development)
 * 2. URL query parameter: ?storeId=suri
 * 3. PostMessage from parent iframe (future)
 */

export type StoreConfig = {
  storeId: string
  designsFile: string
  fontsFile: string | null
}

// Hardcode this for development - set to null to use query params/defaults
// const DEV_STORE_ID: string | null = 'suri'
const DEV_STORE_ID: string | null = null

// Store configuration mapping
const STORE_CONFIGS: Record<string, Omit<StoreConfig, 'storeId'>> = {
  default: {
    designsFile: 'default',
    fontsFile: null, // Uses Google Fonts only
  },
  simple: {
    designsFile: 'simple-portrait',
    fontsFile: null,
  },
  'simple-portrait': {
    designsFile: 'simple-portrait',
    fontsFile: null,
  },
  'simple-landscape': {
    designsFile: 'simple-landscape',
    fontsFile: null,
  },
  suri: {
    designsFile: 'suri',
    fontsFile: 'suri',
  },
}

export function useStoreConfig() {
  const route = useRoute()

  // Determine store ID from various sources
  const storeId = computed<string>(() => {
    // 1. Dev override takes priority
    if (DEV_STORE_ID) {
      return DEV_STORE_ID
    }

    // 2. URL query parameter
    const queryStoreId = route.query.storeId
    if (typeof queryStoreId === 'string' && queryStoreId) {
      return queryStoreId
    }

    // 3. Default
    return 'default'
  })

  // Get full config for the store
  const config = computed<StoreConfig>(() => {
    const id = storeId.value
    const storeConfig = STORE_CONFIGS[id] || STORE_CONFIGS.default!

    return {
      storeId: id,
      ...storeConfig,
    } as StoreConfig
  })

  // Convenience getters
  const designsFile = computed(() => config.value.designsFile)
  const fontsFile = computed(() => config.value.fontsFile)
  const isCustomStore = computed(() => storeId.value !== 'default')

  return {
    storeId,
    config,
    designsFile,
    fontsFile,
    isCustomStore,
  }
}
